/**
 * API client service for AdVerdict backend
 * Provides type-safe fetch wrapper with authentication and error handling
 */

import type { TaxonomySchema, SaveTaxonomySchemaRequest, TaxonomyAnalyticsResponse } from '../types/taxonomy';
import type {
  Template,
  TemplateListResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ForkTemplateRequest,
  DeleteTemplateResponse,
  CustomKpiCatalogResponse,
  CustomKpiDefinition,
  CustomKpiMigrationPreviewResponse,
  CustomKpiMigrationRequest,
  CustomKpiMigrationResponse,
  CustomKpiUsageResponse,
  CreateCustomKpiRequest,
  UpdateCustomKpiRequest,
} from '../types/template';

import { fetchAuthSession } from './auth';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiErrorResponse {
  error: ApiError;
  meta: {
    requestId: string;
    timestamp: string;
  };
}

export interface ApiSuccessResponse<T> {
  data: T;
  meta: {
    requestId: string;
    timestamp: string;
  };
}

export type ApiResponse<T> = ApiSuccessResponse<T>;

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('Missing VITE_API_URL environment variable');
}

// ============================================================================
// ERROR CLASSES
// ============================================================================

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class UnauthorizedError extends ApiClientError {
  constructor(message: string = 'Unauthorized') {
    super(message, 'UNAUTHORIZED', 401);
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends ApiClientError {
  constructor(message: string = 'Forbidden') {
    super(message, 'FORBIDDEN', 403);
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends ApiClientError {
  constructor(message: string = 'Not found') {
    super(message, 'NOT_FOUND', 404);
    this.name = 'NotFoundError';
  }
}

export class ValidationError extends ApiClientError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, 'VALIDATION_ERROR', 400, details);
    this.name = 'ValidationError';
  }
}

// ============================================================================
// REQUEST OPTIONS
// ============================================================================

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
  skipAuth?: boolean;
}

// ============================================================================
// CORE FETCH FUNCTION
// ============================================================================

async function fetchWithAuth<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  options: RequestOptions & { body?: unknown } = {}
): Promise<T> {
  const { headers = {}, params, body, timeout = 30000, skipAuth = false } = options;

  // Build URL with query parameters
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Get auth token (unless skipAuth is true)
  if (!skipAuth) {
    const session = await fetchAuthSession();
    const token = session?.idToken;

    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url.toString(), {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle HTTP errors
    if (!response.ok) {
      await handleErrorResponse(response);
    }

    // Handle 204 No Content (common for DELETE)
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse response
    const data = await response.json();

    // Extract data from success response envelope
    if ('data' in data) {
      return data.data as T;
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiClientError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new NetworkError('Request timeout');
      }
      throw new NetworkError(`Network error: ${error.message}`);
    }

    throw new NetworkError('Unknown error occurred');
  }
}

/**
 * Handle error responses from the API
 */
async function handleErrorResponse(response: Response): Promise<never> {
  let errorData: ApiErrorResponse | null = null;

  try {
    errorData = await response.json();
  } catch {
    // Response is not JSON
  }

  const errorMessage = errorData?.error?.message || response.statusText || 'Unknown error';
  const errorCode = errorData?.error?.code || 'UNKNOWN_ERROR';
  const errorDetails = errorData?.error?.details;

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage, errorDetails);
    default:
      throw new ApiClientError(errorMessage, errorCode, response.status, errorDetails);
  }
}

// ============================================================================
// API CLIENT OBJECT
// ============================================================================

export const api = {
  /**
   * Perform a GET request
   */
  get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return fetchWithAuth<T>(endpoint, 'GET', options);
  },

  /**
   * Perform a POST request
   */
  post<T, D = unknown>(endpoint: string, data?: D, options: RequestOptions = {}): Promise<T> {
    return fetchWithAuth<T>(endpoint, 'POST', { ...options, body: data });
  },

  /**
   * Perform a PUT request
   */
  put<T, D = unknown>(endpoint: string, data?: D, options: RequestOptions = {}): Promise<T> {
    return fetchWithAuth<T>(endpoint, 'PUT', { ...options, body: data });
  },

  /**
   * Perform a PATCH request
   */
  patch<T, D = unknown>(endpoint: string, data?: D, options: RequestOptions = {}): Promise<T> {
    return fetchWithAuth<T>(endpoint, 'PATCH', { ...options, body: data });
  },

  /**
   * Perform a DELETE request
   */
  delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return fetchWithAuth<T>(endpoint, 'DELETE', options);
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build query string from parameters
 */
export function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

// ============================================================================
// TAXONOMY API
// ============================================================================

/**
 * Get the taxonomy schema for a project
 */
export async function getTaxonomySchema(projectId: string): Promise<TaxonomySchema | null> {
  return api.get<TaxonomySchema | null>(`/projects/${projectId}/taxonomy-schema`);
}

/**
 * Save or update the taxonomy schema for a project
 */
export async function saveTaxonomySchema(
  projectId: string,
  data: SaveTaxonomySchemaRequest
): Promise<TaxonomySchema> {
  return api.put<TaxonomySchema>(`/projects/${projectId}/taxonomy-schema`, data);
}

/**
 * Delete the taxonomy schema for a project
 */
export async function deleteTaxonomySchema(projectId: string): Promise<void> {
  return api.delete(`/projects/${projectId}/taxonomy-schema`);
}

// ============================================================================
// TAXONOMY VALUES API (Creative-level)
// ============================================================================

export interface TaxonomyValuesResponse {
  taxonomyExtraction: import('../types/taxonomy').TaxonomyExtraction | null;
  hasAnalysis: boolean;
  analysisId?: string;
  analysisStatus?: string;
}

export interface UpdateTaxonomyValueRequest {
  fieldId: string;
  optionId: string;
}

export interface UpdateTaxonomyValuesResponse {
  taxonomyExtraction: import('../types/taxonomy').TaxonomyExtraction;
  analysisId: string;
}

export interface ReExtractTaxonomyResponse {
  analysisId: string;
  status: string;
  preservedValuesCount: number;
}

/**
 * Get taxonomy values for a creative
 */
export async function getTaxonomyValues(creativeId: string): Promise<TaxonomyValuesResponse> {
  return api.get<TaxonomyValuesResponse>(`/creatives/${creativeId}/taxonomy-values`);
}

/**
 * Update taxonomy values for a creative (user override)
 */
export async function updateTaxonomyValues(
  creativeId: string,
  values: UpdateTaxonomyValueRequest[]
): Promise<UpdateTaxonomyValuesResponse> {
  return api.put<UpdateTaxonomyValuesResponse>(`/creatives/${creativeId}/taxonomy-values`, {
    values,
  });
}

/**
 * Trigger taxonomy re-extraction for a creative
 */
export async function reExtractTaxonomy(
  creativeId: string,
  preserveUserConfirmed: boolean = true
): Promise<ReExtractTaxonomyResponse> {
  return api.post<ReExtractTaxonomyResponse>(`/creatives/${creativeId}/re-extract-taxonomy`, {
    preserveUserConfirmed,
  });
}

// ============================================================================
// TAXONOMY ANALYTICS API
// ============================================================================

/**
 * Get aggregated taxonomy analytics for a project
 */
export async function getTaxonomyAnalytics(projectId: string): Promise<TaxonomyAnalyticsResponse> {
  return api.get<TaxonomyAnalyticsResponse>(`/projects/${projectId}/taxonomy-analytics`);
}

// ============================================================================
// TEMPLATE API
// ============================================================================

/**
 * List all templates for a team (includes starter template metadata)
 */
export async function getTemplates(teamId: string): Promise<TemplateListResponse> {
  return api.get<TemplateListResponse>(`/teams/${teamId}/templates`);
}

/**
 * Get a single template by ID
 */
export async function getTemplate(teamId: string, templateId: string): Promise<Template> {
  return api.get<Template>(`/teams/${teamId}/templates/${templateId}`);
}

/**
 * Create a new template with custom gates
 */
export async function createTemplate(
  teamId: string,
  data: CreateTemplateRequest
): Promise<Template> {
  return api.post<Template>(`/teams/${teamId}/templates`, data);
}

/**
 * Update an existing template
 */
export async function updateTemplate(
  teamId: string,
  templateId: string,
  data: UpdateTemplateRequest
): Promise<Template> {
  return api.put<Template>(`/teams/${teamId}/templates/${templateId}`, data);
}

/**
 * Delete a template (reverts assigned projects to default)
 */
export async function deleteTemplate(
  teamId: string,
  templateId: string
): Promise<DeleteTemplateResponse> {
  return api.delete<DeleteTemplateResponse>(`/teams/${teamId}/templates/${templateId}`);
}

/**
 * Fork a starter template or existing template into a new team template
 */
export async function forkTemplate(
  teamId: string,
  data: ForkTemplateRequest
): Promise<Template> {
  return api.post<Template>(`/teams/${teamId}/templates/fork`, data);
}

/**
 * Duplicate an existing team template
 */
export async function duplicateTemplate(
  teamId: string,
  templateId: string
): Promise<Template> {
  return api.post<Template>(`/teams/${teamId}/templates/${templateId}/duplicate`);
}

// ============================================================================
// CUSTOM KPI API
// ============================================================================

/**
 * List team Custom KPI definitions.
 */
export async function getCustomKpis(
  teamId: string,
  includeArchived: boolean = false
): Promise<CustomKpiDefinition[]> {
  return api.get<CustomKpiDefinition[]>(`/teams/${teamId}/custom-kpis`, {
    params: { includeArchived },
  });
}

/**
 * Get Custom KPI authoring catalog metadata.
 */
export async function getCustomKpiCatalog(teamId: string): Promise<CustomKpiCatalogResponse> {
  return api.get<CustomKpiCatalogResponse>(`/teams/${teamId}/custom-kpis/catalog`);
}

/**
 * Create a team Custom KPI definition.
 */
export async function createCustomKpi(
  teamId: string,
  data: CreateCustomKpiRequest
): Promise<CustomKpiDefinition> {
  return api.post<CustomKpiDefinition>(`/teams/${teamId}/custom-kpis`, data);
}

/**
 * Create a new immutable version of a team Custom KPI definition.
 */
export async function createCustomKpiVersion(
  teamId: string,
  customKpiId: string,
  data: UpdateCustomKpiRequest
): Promise<CustomKpiDefinition> {
  return api.post<CustomKpiDefinition>(
    `/teams/${teamId}/custom-kpis/${customKpiId}/versions`,
    data
  );
}

/**
 * Update a team Custom KPI definition, creating a new backend version.
 */
export async function updateCustomKpi(
  teamId: string,
  customKpiId: string,
  data: UpdateCustomKpiRequest
): Promise<CustomKpiDefinition> {
  return api.put<CustomKpiDefinition>(`/teams/${teamId}/custom-kpis/${customKpiId}`, data);
}

/**
 * Archive a team Custom KPI definition.
 */
export async function archiveCustomKpi(
  teamId: string,
  customKpiId: string
): Promise<{ archived: boolean }> {
  return api.delete<{ archived: boolean }>(`/teams/${teamId}/custom-kpis/${customKpiId}`);
}

/**
 * Get templates currently pinned to a Custom KPI.
 */
export async function getCustomKpiUsage(
  teamId: string,
  customKpiId: string
): Promise<CustomKpiUsageResponse> {
  return api.get<CustomKpiUsageResponse>(
    `/teams/${teamId}/custom-kpis/${customKpiId}/usage`
  );
}

/**
 * Preview selected template migration impact for a Custom KPI version.
 */
export async function previewCustomKpiMigration(
  teamId: string,
  customKpiId: string,
  data: CustomKpiMigrationRequest
): Promise<CustomKpiMigrationPreviewResponse> {
  return api.post<CustomKpiMigrationPreviewResponse>(
    `/teams/${teamId}/custom-kpis/${customKpiId}/migrations/preview`,
    data
  );
}

/**
 * Migrate selected template version pins for a Custom KPI.
 */
export async function migrateCustomKpiTemplates(
  teamId: string,
  customKpiId: string,
  data: CustomKpiMigrationRequest
): Promise<CustomKpiMigrationResponse> {
  return api.post<CustomKpiMigrationResponse>(
    `/teams/${teamId}/custom-kpis/${customKpiId}/migrations`,
    data
  );
}
